import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Download, FileSpreadsheet, FileText, Filter, RefreshCw, 
  Calendar, User, CheckCircle, XCircle, Clock, Search, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const PaymentCollectionHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cashiers, setCashiers] = useState([]);
  
  // Filters
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedCashier, setSelectedCashier] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedCashier, selectedStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = new URLSearchParams({
        from_date: fromDate,
        to_date: toDate
      });
      
      if (selectedCashier !== 'all') {
        params.append('cashier_id', selectedCashier);
      }
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      
      const response = await axios.get(`${API}/payment-collection-history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setData(response.data);
      setCashiers(response.data.cashiers || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to fetch payment collection history');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toFixed(2)}`;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium inline-flex items-center gap-1"><CheckCircle size={12} /> Approved</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium inline-flex items-center gap-1"><XCircle size={12} /> Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium inline-flex items-center gap-1"><Clock size={12} /> Pending</span>;
    }
  };

  // Filter payments by search query
  const filteredPayments = (data?.payments || []).filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (p.customer_name || '').toLowerCase().includes(query) ||
      (p.customer_mobile || '').includes(query) ||
      (p.collected_by_name || '').toLowerCase().includes(query) ||
      (p.reference || '').toLowerCase().includes(query)
    );
  });

  // Export to PDF
  const exportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.text('Payment Collection History', pageWidth / 2, 15, { align: 'center' });
    
    // Date range
    doc.setFontSize(10);
    doc.text(`Period: ${formatDateOnly(fromDate)} to ${formatDateOnly(toDate)}`, pageWidth / 2, 22, { align: 'center' });
    
    // Summary
    doc.setFontSize(11);
    doc.text(`Total Collections: ${formatCurrency(data?.total_amount || 0)}`, 14, 32);
    doc.text(`Total Entries: ${data?.total_count || 0}`, 14, 38);
    doc.text(`Approved: ${formatCurrency(data?.status_summary?.approved || 0)}`, 80, 32);
    doc.text(`Pending: ${formatCurrency(data?.status_summary?.pending || 0)}`, 80, 38);
    doc.text(`Rejected: ${formatCurrency(data?.status_summary?.rejected || 0)}`, 140, 32);
    
    // Table
    const tableData = filteredPayments.map((p, idx) => [
      idx + 1,
      formatDateTime(p.created_at),
      p.customer_name || '-',
      p.collected_by_name || 'Owner',
      formatCurrency(p.amount),
      (p.payment_mode || 'Cash').toUpperCase(),
      (p.status || 'pending').toUpperCase(),
      p.approved_at ? formatDateTime(p.approved_at) : '-'
    ]);
    
    doc.autoTable({
      startY: 45,
      head: [['#', 'Collection Date', 'Customer', 'Collected By', 'Amount', 'Mode', 'Status', 'Action Date']],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [99, 102, 241] },
      alternateRowStyles: { fillColor: [248, 250, 252] }
    });
    
    // Cashier summary
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text('Summary by Cashier:', 14, finalY);
    
    const cashierData = (data?.cashier_summary || []).map(c => [
      c.cashier_name,
      c.current_name !== c.cashier_name ? `(Now: ${c.current_name})` : '',
      c.count,
      formatCurrency(c.total)
    ]);
    
    doc.autoTable({
      startY: finalY + 5,
      head: [['Cashier Name (At Collection)', 'Current Name', 'Count', 'Total Amount']],
      body: cashierData,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [16, 185, 129] }
    });
    
    doc.save(`payment-collection-history-${fromDate}-to-${toDate}.pdf`);
    toast.success('PDF exported successfully');
  };

  // Export to Excel (CSV)
  const exportExcel = () => {
    const headers = ['#', 'Collection Date', 'Customer Name', 'Customer Mobile', 'Collected By', 'Amount', 'Mode', 'Reference', 'Status', 'Action Date', 'Notes'];
    
    const rows = filteredPayments.map((p, idx) => [
      idx + 1,
      formatDateTime(p.created_at),
      p.customer_name || '-',
      p.customer_mobile || '-',
      p.collected_by_name || 'Owner',
      p.amount || 0,
      (p.payment_mode || 'Cash').toUpperCase(),
      p.reference || '-',
      (p.status || 'pending').toUpperCase(),
      p.approved_at ? formatDateTime(p.approved_at) : '-',
      p.notes || '-'
    ]);
    
    // Add summary rows
    rows.push([]);
    rows.push(['SUMMARY']);
    rows.push(['Total Collections', formatCurrency(data?.total_amount || 0)]);
    rows.push(['Total Entries', data?.total_count || 0]);
    rows.push(['Approved Amount', formatCurrency(data?.status_summary?.approved || 0)]);
    rows.push(['Pending Amount', formatCurrency(data?.status_summary?.pending || 0)]);
    rows.push(['Rejected Amount', formatCurrency(data?.status_summary?.rejected || 0)]);
    rows.push([]);
    rows.push(['CASHIER WISE SUMMARY']);
    rows.push(['Cashier', 'Current Name', 'Count', 'Total']);
    (data?.cashier_summary || []).forEach(c => {
      rows.push([c.cashier_name, c.current_name, c.count, formatCurrency(c.total)]);
    });
    
    // Convert to CSV
    const csvContent = [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `payment-collection-history-${fromDate}-to-${toDate}.csv`;
    link.click();
    
    toast.success('Excel (CSV) exported successfully');
  };

  return (
    <div data-testid="payment-collection-history-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-xl">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Payment Collection History</h1>
            <p className="text-slate-500">View and export cashier payment collections</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
          <button
            onClick={exportPDF}
            disabled={!data || filteredPayments.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50"
          >
            <FileText size={18} />
            PDF
          </button>
          <button
            onClick={exportExcel}
            disabled={!data || filteredPayments.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50"
          >
            <FileSpreadsheet size={18} />
            Excel
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-slate-500" />
          <span className="font-medium text-slate-700">Filters</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Date From */}
          <div>
            <label className="block text-sm text-slate-600 mb-1">From Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
          
          {/* Date To */}
          <div>
            <label className="block text-sm text-slate-600 mb-1">To Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
          
          {/* Cashier Filter */}
          <div>
            <label className="block text-sm text-slate-600 mb-1">Cashier</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <select
                value={selectedCashier}
                onChange={(e) => setSelectedCashier(e.target.value)}
                className="w-full pl-10 pr-8 py-2 border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
              >
                <option value="all">All (Cashiers + Owner)</option>
                <option value="owner">Owner Only</option>
                {cashiers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm text-slate-600 mb-1">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          
          {/* Search */}
          <div>
            <label className="block text-sm text-slate-600 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Customer, mobile..."
                className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Total Collections</p>
            <p className="text-2xl font-bold">{formatCurrency(data.total_amount)}</p>
            <p className="text-xs opacity-70">{data.total_count} entries</p>
          </div>
          <div className="bg-green-500 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Approved</p>
            <p className="text-2xl font-bold">{formatCurrency(data.status_summary?.approved || 0)}</p>
          </div>
          <div className="bg-amber-500 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Pending</p>
            <p className="text-2xl font-bold">{formatCurrency(data.status_summary?.pending || 0)}</p>
          </div>
          <div className="bg-red-500 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Rejected</p>
            <p className="text-2xl font-bold">{formatCurrency(data.status_summary?.rejected || 0)}</p>
          </div>
          <div className="bg-slate-700 rounded-xl p-4 text-white">
            <p className="text-sm opacity-80">Cashiers</p>
            <p className="text-2xl font-bold">{data.cashier_summary?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Cashier-wise Summary */}
      {data && data.cashier_summary && data.cashier_summary.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <User size={18} />
            Cashier-wise Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.cashier_summary.map(c => (
              <div 
                key={c.cashier_id} 
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedCashier === c.cashier_id 
                    ? 'border-violet-500 bg-violet-50' 
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
                onClick={() => setSelectedCashier(selectedCashier === c.cashier_id ? 'all' : c.cashier_id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-800">{c.cashier_name}</p>
                    {c.current_name !== c.cashier_name && (
                      <p className="text-xs text-slate-500">Now: {c.current_name}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-violet-600">{formatCurrency(c.total)}</p>
                    <p className="text-xs text-slate-500">{c.count} entries</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-violet-200 border-t-violet-600"></div>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="mx-auto mb-4 text-slate-300" size={48} />
            <p className="text-slate-500">No payment records found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting the filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Collection Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Collected By</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Mode</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Action Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Reference</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredPayments.map((p, idx) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm text-slate-800">{formatDateTime(p.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-slate-800">{p.customer_name}</p>
                      <p className="text-xs text-slate-500">{p.customer_mobile}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{p.collected_by_name || 'Owner'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-violet-600 text-right">{formatCurrency(p.amount)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 uppercase">{p.payment_mode || 'Cash'}</td>
                    <td className="px-4 py-3">{getStatusBadge(p.status)}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {p.approved_at ? formatDateTime(p.approved_at) : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500">{p.reference || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentCollectionHistory;
